import { GripVerticalIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import {
  Group,
  Panel,
  Separator,
  type LayoutStorage,
  useDefaultLayout,
} from 'react-resizable-panels';

import { cn } from '@/lib/utils';

/**
 * Panes the reader can resize, on `react-resizable-panels` (shadcn's
 * `Resizable`, on the library's v4 API). The library owns pointer and
 * keyboard resizing and the ARIA window-splitter semantics; this file only
 * dresses the separator in the app's tokens.
 */
function ResizablePanelGroup({
  className,
  ...props
}: ComponentProps<typeof Group>) {
  return (
    <Group
      data-slot="resizable-panel-group"
      className={cn('flex h-full w-full', className)}
      {...props}
    />
  );
}

function ResizablePanel({ ...props }: ComponentProps<typeof Panel>) {
  return <Panel data-slot="resizable-panel" {...props} />;
}

/**
 * The divider between two panes: a hairline that widens its hit area and shows
 * a grip on hover and focus. Focusable, so arrow keys resize too.
 */
function ResizableHandle({
  className,
  ...props
}: ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="resizable-handle"
      className={cn(
        'group/handle bg-border focus-visible:ring-ring relative flex w-px shrink-0 items-center justify-center outline-none focus-visible:ring-3',
        // A wider invisible target than the 1px line, so it can be caught.
        'after:absolute after:inset-y-0 after:left-1/2 after:w-2 after:-translate-x-1/2',
        'hover:bg-ring data-[separator=active]:bg-ring',
        className,
      )}
      {...props}
    >
      <div className="bg-border text-muted-foreground z-10 flex h-6 w-3 items-center justify-center rounded-sm border opacity-0 transition-opacity group-hover/handle:opacity-100 group-focus-visible/handle:opacity-100">
        <GripVerticalIcon aria-hidden="true" className="size-2.5" />
      </div>
    </Separator>
  );
}

/**
 * Storage that never throws: private browsing or a full quota costs only the
 * remembered size, never the page.
 */
const rememberedLayouts: LayoutStorage = {
  getItem: (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Remembered for this page only.
    }
  },
};

/**
 * A group's layout, remembered on this device under `id`. Spread the result
 * onto `ResizablePanelGroup`.
 */
function useRememberedLayout(id: string, panelIds: string[]) {
  return useDefaultLayout({
    id,
    panelIds,
    storage: rememberedLayouts,
    onlySaveAfterUserInteractions: true,
  });
}

export {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  useRememberedLayout,
};
